import ContentBody from '@op/components/ContentBody';
import {
  SAVED_LIST_DEFAULT_REPORT_OWNER,
  type TReportOwner,
} from '@op/components/ReportPermission';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Button, Icon, notification, Bubble, Dropdown, List } from 'tea-component';
import { connect, useDispatch, useHistory, useLocation, useParams } from 'umi';
import CardCommon from '@op/components/CardCommon';
import { resetSummaryInfo } from '@op/services';
import BubbleCommon, {
  keywordDrillUrlHeaderTips,
  keywordDrillUrlLoadingTips,
  urlDrillUrlHeaderTips,
  KeywordTopicPreviewsTaggedTips,
  KeywordTopicCopyCurrentReportTips,
  KeywordTopicCopyOriginalReportTips,
} from '@op/components/BubbleCommon';
import { DBO_IMG } from '@op/utils/format';
import { uniqueArray } from '@op/utils';
import {
  buriedBtnPoint,
  DBO_POINT,
  useInitBuriedPointInfo,
} from '@op/operationLog/buriedPoint';
import { AIPreview } from '@op/operationLog/pages';
import SvgIcon from '@home/components/SvgIcon';
import KeywordCardAnalysis from './pages/KeywordCardAnalysis';
import URLCardAnalysis from './pages/URLCardAnalysis';
import './scss/PreviewAnalysis.scss';
import { useUpdateSummaryName } from './hook';
import { useUrlSummaryInfo } from './hook/useUrlAnalysisFlow';
import { useKeywordAnalysisDetail } from './hook/useTopicAnalysisFlow';
import useLanguage from '@op/hooks/useLanguage';
import { updateURL } from '../hooks';

export type LocationState = {
  isCurrent: any;
  query: any;
  isSubscription?: boolean;
  source?: string;
  owner?: TReportOwner;
};

export interface ILocationWithQuery {
  state: LocationState;
  pathname: string;
  query?: {
    preview?: string;
    lead_id?: number;
    redirect?: string;
    tab?: string;
    data_type?: string;
    version_id?: string;
  };
}

type RouteParams = {
  id: string;
};

const { Content } = Layout;
const guideInfoId = 'game-analysis-guide-info';
const keywordUrl = '/opinion/AIAnalysisHub/KeywordAnalysis';
const topicUrl = '/opinion/AIAnalysisHub/AITopicAnalysis';
const urlUrl = '/opinion/AIAnalysisHub/URLAnalysis';
function PreviewAnalysis({
  gameInfo,
  wholeGameInfo,
  userInfo,
}: {
  gameInfo: any;
  wholeGameInfo: any;
  userInfo: any;
}) {
  const history = useHistory();
  const { id } = useParams<RouteParams>();
  const location =
    useLocation<LocationState>() as unknown as ILocationWithQuery;
  const { query, state, pathname } = location;
  const isPreviews = query?.preview === '1';
  const dataType = query?.data_type || 'legacy';
  let urlVersionId = Number(query?.version_id || '');
  const isCurrent = state?.isCurrent;
  const pageExtInfo = pathname.includes('KeywordAnalysis')
    ? 'KeywordAnalysis'
    : 'URLAnalysis';
  const isKeywordPage = pageExtInfo === 'KeywordAnalysis';

  useInitBuriedPointInfo({
    buriedPoint: AIPreview,
    triggerPagePoint: false,
  });

  const { formatMessage } = useLanguage();
  const [loadingFlag, setLoadingFlag] = useState<string[]>([]);

  const [screenLoading, setScreenLoading] = useState(false);
  const [gptList, setGptList] = useState<string[]>([]);
  const [showDownloadTips, setShowDownloadTips] = useState(
    localStorage.getItem(guideInfoId) !== 'true',
  );

  const { title, setTitle, toggleWindows, visible, EditSummaryName } =
    useUpdateSummaryName();

  const {
    isEmpty: urlAnalysisIsEmpty,
    summaryInfo: urlSummaryData,
    requestUrlInfo,
    loading: urlSummaryLoading,
  } = useUrlSummaryInfo();

  const {
    loading: keywordAnalysisLoading,
    requestAnalysisDetail,
    detail: keywordAnalysisDetail,
    isTopicAnalysis,
  } = useKeywordAnalysisDetail({ needRead: true });

  const apiLoading = useMemo(() => {
    if (isKeywordPage) {
      return uniqueArray(loadingFlag).length < 2;
    }

    return uniqueArray(loadingFlag).length < 3 + gptList.length;
  }, [loadingFlag, gptList]);

  const setApiLoading = (key: string) => {
    setLoadingFlag((pre) => [...pre, key]);
  };

  function requestUserSummaryInfo() {
    const realId = Number(id);
    if (isKeywordPage) {
      const params: any = {
        task_id: realId,
        data_type: dataType,
        ...gameInfo,
      };

      if (urlVersionId && !isNaN(urlVersionId)) {
        params.version_id = urlVersionId;
      }

      return requestAnalysisDetail(params, {
        callbackStop: (data) => {
          return !!data;
        },
      });
    }

    return requestUrlInfo({ ...gameInfo, id: realId });
  }

  async function resetAnalysis() {
    if (!isCreator) {
      return null;
    }
    try {
      let businessType = 'url';
      if (pageExtInfo === 'KeywordAnalysis') {
        businessType = isTopicAnalysis ? 'topic' : 'keyword';
      }
      const realId = Number(id);
      const filter = {
        ...gameInfo,
        id: realId,
        business_type: businessType,
      };

      if (pageExtInfo === 'KeywordAnalysis') {
        filter.data_type = dataType;
      }

      await resetSummaryInfo(filter);
      notification.success({
        description: formatMessage({ id: 'Reset summary success' }),
      });

      if (businessType !== 'url') {
        updateURL({
          filter: ['version_id']
        });
      }
      urlVersionId = 0;
      const data = await requestUserSummaryInfo();
      return data;
    } catch (error) {
      notification.error({
        description: formatMessage({ id: 'Reset summary failed' }),
      });
      return null;
    }
  }

  useEffect(() => {
    if (id && gameInfo.edition_unified_id) {
      requestUserSummaryInfo();
    }
  }, [id, gameInfo]);

  async function screenshotImage() {
    const element = document.getElementById('dbo-analysis-view-screen-box');
    try {
      await DBO_IMG.screenshot(element, title, {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onclone: (clonedDoc: Document) => {
          // 修复 SVG <use> 引用在 html2canvas 中不渲染的问题：
          // html2canvas 克隆 DOM 后无法解析 <use href="#symbol-id"> 引用，
          // 需要将 <use> 引用的 <symbol> 内容内联展开到每个 <svg> 中
          const useElements = clonedDoc.querySelectorAll('svg use');
          useElements.forEach(useEl => {
            const href = useEl.getAttribute('xlink:href');
            if (!href?.startsWith('#')) return;
            // 从原始文档中查找 symbol 定义（sprite sheet 在原始文档中）
            const symbol = document.querySelector(href);
            if (!symbol) return;
            const svgParent = useEl.closest('svg');
            if (!svgParent) return;
            // 复制 symbol 的 viewBox 到父 svg
            const viewBox = symbol.getAttribute('viewBox');
            if (viewBox) svgParent.setAttribute('viewBox', viewBox);
            // 将 symbol 内部的子元素克隆到 svg 中，替换 <use>
            const fragment = clonedDoc.createDocumentFragment();
            Array.from(symbol.children).forEach(child => {
              fragment.appendChild(child.cloneNode(true));
            });
            svgParent.replaceChild(fragment, useEl);
          });

          const elements = clonedDoc.querySelectorAll(
            '.dbo-vailddropdown > span:first-child',
          );

          Array.from(elements).forEach((element: any) => {
            const parentElement = element.closest('td');
            if (parentElement) {
              parentElement.innerHTML = `<span style="font-size: 12px; color: #666">${element.innerHTML}</span>`;
            }
          });

          const element = clonedDoc.querySelector('.dbo-accountsbox-keywords');
          element?.parentNode?.removeChild(element);

          // Safari/WebKit 兼容性：强制重排确保渲染完成
          const clonedElement = clonedDoc.querySelector(
            '#dbo-analysis-view-screen-box',
          );
          if (clonedElement) {
            void (clonedElement as HTMLElement).offsetHeight;
          }

          return clonedDoc as any;
        },
      });
    } catch (error) {
      console.error(error);
      notification.error({
        description: formatMessage({
          id: 'Report image download failed. Please refresh the page and try again.',
        }),
      });
    }
    setScreenLoading(false);
  }

  useEffect(() => {
    let timer = null;
    if (!apiLoading && !isPreviews) {
      setScreenLoading(true);
      timer = setTimeout(() => {
        screenshotImage();
      }, 2000);
    }

    return () => {
      timer !== null && clearTimeout(timer);
    };
  }, [apiLoading]);

  const {
    loading,
    isCreator,
    leadId,
    createTime,
    shortId,
    originalShortUrl,
    isSubscription,
    replaceParams,
    successTagged,
  } = useMemo(() => {
    if (isKeywordPage) {
      const {
        is_creator,
        create_time,
        short_url,
        task_name,
        keyword_analysis_task_id,
        id,
        data_type,
        original_short_url,
        keyword_analysis_task_type,
        replace_params,
        has_tagged_result,
      } = keywordAnalysisDetail || {};
      const realId = data_type === 'new' ? keyword_analysis_task_id : id;
      setTitle(task_name);
      const isSubscription = keyword_analysis_task_type === 'recurring';
      return {
        loading: keywordAnalysisLoading,
        isCreator: is_creator,
        leadId: realId,
        createTime: create_time,
        shortId: short_url,
        originalShortUrl: original_short_url || short_url,
        isSubscription,
        replaceParams: replace_params,
        successTagged: has_tagged_result,
      };
    }

    const { is_creator, lead_id, create_time, short_url, task_name, name } =
      urlSummaryData || {};
    setTitle(name || task_name);
    return {
      loading: urlSummaryLoading,
      isCreator: is_creator === 1,
      leadId: lead_id,
      createTime: create_time,
      shortId: short_url,
    };
  }, [
    urlSummaryData,
    urlSummaryLoading,
    keywordAnalysisLoading,
    keywordAnalysisDetail,
    urlAnalysisIsEmpty,
  ]);

  const isFirstPage = useRef(true);
  useEffect(() => {
    if (!loading && isFirstPage.current) {
      const isKeyword = pageExtInfo === 'KeywordAnalysis';
      let extInfo = isKeyword ? 'KeywordAnalysis' : 'URLAnalysis';

      if (isTopicAnalysis) extInfo = 'AITopicAnalysis';

      DBO_POINT.page(extInfo);

      isFirstPage.current = false;
    }
  }, [isTopicAnalysis, loading]);

  const updateNameType = useMemo(() => {
    if (!isKeywordPage) {
      return 'url';
    }
    if (isSubscription) {
      return isTopicAnalysis
        ? 'repeat_keyword_analysis_topic_task'
        : 'repeat_keyword_analysis_keyword_task';
    }
    return isTopicAnalysis ? 'keyword_topic' : 'keyword';
  }, [isTopicAnalysis, isSubscription]);

  const dispatch = useDispatch();
  useEffect(() => {
    const isKeyword = pageExtInfo === 'KeywordAnalysis';
    if (!isKeyword) return;

    const selected = isTopicAnalysis ? topicUrl : keywordUrl;

    dispatch({
      type: 'opinionMenu/changeSelectMenuItem',
      payload: loading ? ' ' : selected,
    });

    return () => {
      dispatch({ type: 'opinionMenu/resetSelectMenuItem' });
    };
  }, [isTopicAnalysis, pageExtInfo, loading]);

  function raceLampTips() {
    if (loading) return undefined;

    if (successTagged) return KeywordTopicPreviewsTaggedTips;

    if (isSubscription) return undefined;

    if (isKeywordPage) return keywordDrillUrlHeaderTips(createTime);

    return urlDrillUrlHeaderTips(createTime);
  }

  return (
    <>
      {visible && (
        <EditSummaryName
          toggleWindows={toggleWindows}
          title={title}
          id={Number(id)}
          setTitle={setTitle}
          type={updateNameType}
          dataType={dataType as 'new'}
        ></EditSummaryName>
      )}
      <Content className="dbo-page dbo-page-keyword-url-drill">
        <ContentBody
          horseRaceLampTips={raceLampTips()}
          contentHeard={
            loading ? undefined : (
              <Content.Header
                showBackButton
                onBackButtonClick={() => {
                  if (query?.redirect) {
                    return history.push(query.redirect);
                  }

                  const isKeyword = pageExtInfo === 'KeywordAnalysis';
                  let url = isKeyword ? keywordUrl : urlUrl;

                  if (isTopicAnalysis) url = topicUrl;

                  if (isCurrent) {
                    const ownerQuery =
                      state?.owner &&
                        state.owner !== SAVED_LIST_DEFAULT_REPORT_OWNER
                        ? `&owner=${state.owner}`
                        : '';
                    return history.push(
                      `${url}?tab=savedAnalysisList${ownerQuery}`,
                      state,
                    );
                  }

                  history.push(url);
                }}
                title={
                  <div className="dbo-analysis-title">
                    {title}
                    {isCreator === 1 && (
                      <Bubble content={formatMessage({ id: 'Edit Name' })}>
                        <SvgIcon
                          onClick={() => toggleWindows()}
                          className="dbo-analysis-icon"
                          type="exit-icon"
                        ></SvgIcon>
                      </Bubble>
                    )}
                  </div>
                }
                operation={
                  <>
                    {
                      isSubscription && (
                        <Dropdown
                          button={
                            <Button className='dbo-analysis-copy-btn' onClick={(e) => e?.stopPropagation()}>
                              <SvgIcon
                                className="dbo-analysis-icon"
                                type="share-link"
                              />
                              {formatMessage({ id: 'Share Report' })}
                            </Button>
                          }
                          appearance="pure"
                          matchButtonWidth
                          trigger={'hover'}
                        >
                          <List type="option">
                            <List.Item
                              onClick={() => {
                                const url = window.location.href;
                                navigator.clipboard.writeText(url).then(() => {
                                  notification.success({
                                    description: formatMessage({ id: 'Report link copied' }),
                                  });
                                });
                              }}
                              className='dbo-analysis-copy-btn__item'
                            >
                              <BubbleCommon tipsMap={KeywordTopicCopyCurrentReportTips} showIcon={false} placement='left'>
                                {formatMessage({ id: 'Current Report' })}
                              </BubbleCommon>
                            </List.Item>
                            <List.Item
                              onClick={() => {
                                const url = new URL(window.location.href);
                                url.searchParams.delete('version_id');
                                navigator.clipboard.writeText(url.toString()).then(() => {
                                  notification.success({
                                    description: formatMessage({ id: 'Report link copied' }),
                                  });
                                });
                              }}
                              className='dbo-analysis-copy-btn__item'
                            >
                              <BubbleCommon tipsMap={KeywordTopicCopyOriginalReportTips} showIcon={false} placement='left'>
                                {formatMessage({ id: 'Original Report' })}
                              </BubbleCommon>
                            </List.Item>
                          </List>
                        </Dropdown>
                      )
                    }
                    <BubbleCommon
                      overlayClassName="dbo-download-bubble"
                      dark={false}
                      tipsMap={null}
                      showIcon={false}
                      placement="left-start"
                      visible={showDownloadTips}
                      content={
                        <div className="dbo-download-tips">
                          <div className="dbo-download-tips-title">
                            {formatMessage({ id: 'Download Image' })}
                          </div>
                          <div className="dbo-download-tips-desc">
                            {formatMessage({
                              id: 'After the data is loaded, the report image can be downloaded.',
                            })}
                          </div>
                          <div>
                            <div className="dbo-download-tips-buttons">
                              <Button
                                type="primary"
                                onClick={() => {
                                  localStorage.setItem(guideInfoId, 'true');
                                  setShowDownloadTips(false);
                                }}
                              >
                                {formatMessage({ id: 'Confirm' })}
                              </Button>
                            </div>
                          </div>
                        </div>
                      }
                    >
                      <BubbleCommon
                        tipsMap={
                          isPreviews && apiLoading
                            ? keywordDrillUrlLoadingTips
                            : null
                        }
                        placement="left-start"
                        showIcon={false}
                      >
                        <Button
                          type="weak"
                          className="dbo-analysis-download-btn"
                          disabled={screenLoading || apiLoading}
                          onClick={() => {
                            setScreenLoading(true);
                            screenshotImage();
                            buriedBtnPoint('DownloadAnalysis');
                          }}
                        >
                          {screenLoading || (apiLoading && !isPreviews) ? (
                            <Icon style={{ marginRight: '6px' }} type="loading" />
                          ) : (
                            <SvgIcon type="download" className="download" />
                          )}
                          {formatMessage({ id: 'Image download' })}
                        </Button>
                      </BubbleCommon>
                    </BubbleCommon>
                  </>
                }
              ></Content.Header>
            )
          }
        >
          <CardCommon contentCard loading={loading} loadingHeight={700}>
            {isKeywordPage ? (
              <KeywordCardAnalysis
                gameInfo={gameInfo}
                wholeGameInfo={wholeGameInfo}
                isPreviews
                isCreator={isCreator}
                shortId={shortId}
                leadId={leadId}
                dataType={dataType as 'new'}
                resetAnalysis={resetAnalysis}
                userInfo={userInfo}
                replaceParams={replaceParams}
                versionId={
                  urlVersionId && !isNaN(urlVersionId)
                    ? urlVersionId
                    : undefined
                }
                setApiLoading={setApiLoading}
                originalShortUrl={originalShortUrl}
                backGptInfo={(list: string[]) => setGptList(list)}
                isTopicAnalysis={isTopicAnalysis}
                isSubscription={isSubscription}
                successTagged={successTagged}
                title={title}
              ></KeywordCardAnalysis>
            ) : (
              <URLCardAnalysis
                gameInfo={gameInfo}
                wholeGameInfo={wholeGameInfo}
                isPreviews
                isCreator={isCreator}
                resetAnalysis={resetAnalysis}
                shortId={shortId}
                leadId={leadId}
                userInfo={userInfo}
                setApiLoading={setApiLoading}
                backGptInfo={(list: string[]) => setGptList(list)}
              ></URLCardAnalysis>
            )}
          </CardCommon>
        </ContentBody>
      </Content>
    </>
  );
}

export default connect((state: any) => {
  const { opinionCommon, opinionFilter, home } = state;
  return {
    gameInfo: opinionCommon.gameInfo,
    filter: opinionFilter,
    wholeGameInfo: opinionCommon.wholeGameInfo,
    userInfo: home.userInfo,
  };
})(PreviewAnalysis);
